import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Instagram, Linkedin, Music2, Sparkles, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";

const GOALS = [
    { id: "creator", label: "Creator", desc: "Build a creative audience" },
    { id: "influencer", label: "Influencer", desc: "Grow reach & monetize" },
    { id: "professional", label: "Professional", desc: "Career & authority" },
    { id: "entrepreneur", label: "Entrepreneur", desc: "Sell & build a brand" },
];

const PLATFORMS = [
    { key: "instagram", label: "Instagram", icon: Instagram, placeholder: "https://instagram.com/yourhandle", followers: ["<1K", "1K-10K", "10K-100K", "100K+"] },
    { key: "tiktok", label: "TikTok", icon: Music2, placeholder: "https://tiktok.com/@yourhandle", followers: ["<1K", "1K-10K", "10K-100K", "100K+"] },
    { key: "linkedin", label: "LinkedIn", icon: Linkedin, placeholder: "https://linkedin.com/in/yourhandle", followers: ["<500", "500-2K", "2K-10K", "10K+"] },
];

const FREQ = ["daily", "weekly", "monthly", "rarely"];
const CONTENT_TYPES = ["education", "entertainment", "behind-the-scenes", "thought leadership", "product", "lifestyle"];

export default function Onboarding() {
    const { refresh } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [name, setName] = useState("");
    const [goal, setGoal] = useState("creator");
    const [description, setDescription] = useState("");
    const [yearsOfExperience, setYears] = useState(2);
    const [primaryContentNiche, setNiche] = useState("");
    const [currentBrandFeel, setBrandFeel] = useState("");
    const [anchors, setAnchors] = useState({
        instagram: { url: "", metrics: { followers: "", postFrequency: "", contentType: [] } },
        tiktok: { url: "", metrics: { followers: "", postFrequency: "", contentType: [] } },
        linkedin: { url: "", metrics: { followers: "", postFrequency: "", contentType: [] } },
    });
    const [submitting, setSubmitting] = useState(false);

    const updateAnchor = (k, field, value) =>
        setAnchors((a) => ({ ...a, [k]: { ...a[k], [field]: value } }));
    const updateMetric = (k, field, value) =>
        setAnchors((a) => ({ ...a, [k]: { ...a[k], metrics: { ...a[k].metrics, [field]: value } } }));
    const toggleContentType = (k, t) =>
        setAnchors((a) => {
            const cur = a[k].metrics.contentType;
            const next = cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t];
            return { ...a, [k]: { ...a[k], metrics: { ...a[k].metrics, contentType: next } } };
        });

    const submit = async () => {
        if (description.length < 20) return toast.error("Description must be at least 20 characters");
        if (!primaryContentNiche || !currentBrandFeel) return toast.error("Fill all metrics");
        setSubmitting(true);
        try {
            const payload = {
                name,
                goal,
                description,
                anchors: Object.fromEntries(
                    Object.entries(anchors)
                        .filter(([, v]) => v.url?.trim())
                        .map(([k, v]) => [
                            k,
                            {
                                url: v.url.trim(),
                                metrics: v.metrics.followers
                                    ? {
                                          followers: v.metrics.followers,
                                          postFrequency: v.metrics.postFrequency || "weekly",
                                          contentType: v.metrics.contentType,
                                      }
                                    : null,
                            },
                        ]),
                ),
                selfReportedMetrics: {
                    yearsOfExperience: Number(yearsOfExperience),
                    primaryContentNiche,
                    currentBrandFeel,
                },
            };
            await api.post("/ai/analyze", payload);
            await refresh();
            toast.success("Your AURA is ready");
            navigate("/dashboard");
        } catch (err) {
            const status = err?.response?.status;
            if (status === 429) {
                toast.error("🤖 AI engine is on pause — rate limit reached. Try again in a few minutes.", {
                    duration: 60000,
                });
            } else {
                toast.error(err?.response?.data?.detail || err?.response?.data?.error?.message || "Analysis failed");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Layout>
            <Toaster theme="light" position="top-center" />
            <div className="mb-8">
                <div className="font-jetbrains-mono text-xs tracking-[0.3em] text-aura-violet mb-3">
                    // ONBOARDING / STEP {step} of 3
                </div>
                <h1 className="font-syne text-4xl sm:text-5xl font-bold tracking-tight text-aura-text">
                    Let's calibrate your <span className="text-grad-violet">signal</span>.
                </h1>
            </div>

            <motion.div
                key={step}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass p-6 lg:p-10 space-y-6"
                data-testid={`onboarding-step-${step}`}
            >
                {step === 1 && (
                    <>
                        <h2 className="font-syne text-2xl">Who are you?</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="font-jetbrains-mono text-[10px] uppercase tracking-[0.2em] text-aura-muted">Display name</label>
                                <input
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="mt-1.5 w-full bg-aura-input text-aura-text border border-aura-violet/15 rounded-lg px-4 py-3 text-sm focus:border-aura-violet focus:outline-none"
                                    data-testid="onboarding-name-input"
                                />
                            </div>
                            <div>
                                <label className="font-jetbrains-mono text-[10px] uppercase tracking-[0.2em] text-aura-muted">Years in your niche</label>
                                <input
                                    type="number"
                                    min={0}
                                    max={50}
                                    value={yearsOfExperience}
                                    onChange={(e) => setYears(e.target.value)}
                                    className="mt-1.5 w-full bg-aura-input text-aura-text border border-aura-violet/15 rounded-lg px-4 py-3 text-sm focus:border-aura-violet focus:outline-none"
                                    data-testid="onboarding-years-input"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="font-jetbrains-mono text-[10px] uppercase tracking-[0.2em] text-aura-muted">Your goal</label>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mt-2">
                                {GOALS.map((g) => (
                                    <motion.button
                                        key={g.id}
                                        type="button"
                                        whileTap={{ scale: 0.97 }}
                                        onClick={() => setGoal(g.id)}
                                        className={`p-4 rounded-xl border text-left transition ${
                                            goal === g.id
                                                ? "border-aura-violet bg-aura-violet/10 glow-violet-soft"
                                                : "border-aura-violet/15 bg-white hover:border-aura-violet/40"
                                        }`}
                                        data-testid={`goal-${g.id}`}
                                    >
                                        <div className="font-syne font-semibold">{g.label}</div>
                                        <div className="text-xs text-aura-muted mt-0.5">{g.desc}</div>
                                    </motion.button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="font-jetbrains-mono text-[10px] uppercase tracking-[0.2em] text-aura-muted">
                                Describe yourself & your work (20-500 chars)
                            </label>
                            <textarea
                                rows={4}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                maxLength={500}
                                className="mt-1.5 w-full bg-aura-input text-aura-text border border-aura-violet/15 rounded-lg px-4 py-3 text-sm focus:border-aura-violet focus:outline-none"
                                data-testid="onboarding-description-input"
                            />
                            <div className="text-right text-xs text-aura-dim font-jetbrains-mono mt-1">{description.length}/500</div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="font-jetbrains-mono text-[10px] uppercase tracking-[0.2em] text-aura-muted">Primary niche</label>
                                <input
                                    value={primaryContentNiche}
                                    onChange={(e) => setNiche(e.target.value)}
                                    placeholder="e.g. AI productivity, fashion, fintech"
                                    className="mt-1.5 w-full bg-aura-input text-aura-text border border-aura-violet/15 rounded-lg px-4 py-3 text-sm focus:border-aura-violet focus:outline-none"
                                    data-testid="onboarding-niche-input"
                                />
                            </div>
                            <div>
                                <label className="font-jetbrains-mono text-[10px] uppercase tracking-[0.2em] text-aura-muted">Current brand feel</label>
                                <input
                                    value={currentBrandFeel}
                                    onChange={(e) => setBrandFeel(e.target.value)}
                                    placeholder="e.g. minimal, edgy, professional"
                                    className="mt-1.5 w-full bg-aura-input text-aura-text border border-aura-violet/15 rounded-lg px-4 py-3 text-sm focus:border-aura-violet focus:outline-none"
                                    data-testid="onboarding-brandfeel-input"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end pt-2">
                            <motion.button
                                whileTap={{ scale: 0.97 }}
                                onClick={() => {
                                    if (!name) return toast.error("Tell us your name");
                                    setStep(2);
                                }}
                                className="px-6 py-3 rounded-lg bg-gradient-to-r from-aura-violet to-aura-glow text-white font-dm-sans font-medium glow-violet-soft btn-ripple flex items-center gap-2"
                                data-testid="onboarding-next-1-btn"
                            >
                                Next <ArrowRight className="w-4 h-4" />
                            </motion.button>
                        </div>
                    </>
                )}

                {step === 2 && (
                    <>
                        <h2 className="font-syne text-2xl">Connect your social anchors</h2>
                        <p className="text-sm text-aura-body -mt-3">
                            Optional but recommended. Paste your profile URLs and add metrics for a sharper score.
                        </p>
                        <div className="space-y-5">
                            {PLATFORMS.map((p) => (
                                <div key={p.key} className="border border-[rgba(124,58,237,0.15)] rounded-xl p-5">
                                    <div className="flex items-center gap-2 mb-3">
                                        <p.icon className="w-4 h-4 text-aura-violet" />
                                        <span className="font-syne font-semibold">{p.label}</span>
                                    </div>
                                    <input
                                        value={anchors[p.key].url}
                                        onChange={(e) => updateAnchor(p.key, "url", e.target.value)}
                                        placeholder={p.placeholder}
                                        className="w-full bg-aura-input text-aura-text border border-aura-violet/15 rounded-lg px-4 py-2.5 text-sm focus:border-aura-violet focus:outline-none"
                                        data-testid={`anchor-${p.key}-url`}
                                    />
                                    {anchors[p.key].url && (
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                                            <select
                                                value={anchors[p.key].metrics.followers}
                                                onChange={(e) => updateMetric(p.key, "followers", e.target.value)}
                                                className="bg-aura-input text-aura-text border border-aura-violet/15 rounded-lg px-3 py-2 text-sm focus:border-aura-violet focus:outline-none"
                                                data-testid={`anchor-${p.key}-followers`}
                                            >
                                                <option value="">Followers (range)</option>
                                                {p.followers.map((f) => <option key={f} value={f}>{f}</option>)}
                                            </select>
                                            <select
                                                value={anchors[p.key].metrics.postFrequency}
                                                onChange={(e) => updateMetric(p.key, "postFrequency", e.target.value)}
                                                className="bg-aura-input text-aura-text border border-aura-violet/15 rounded-lg px-3 py-2 text-sm focus:border-aura-violet focus:outline-none"
                                                data-testid={`anchor-${p.key}-freq`}
                                            >
                                                <option value="">Frequency</option>
                                                {FREQ.map((f) => <option key={f} value={f}>{f}</option>)}
                                            </select>
                                            <div className="flex flex-wrap gap-1.5">
                                                {CONTENT_TYPES.map((t) => (
                                                    <button
                                                        type="button"
                                                        key={t}
                                                        onClick={() => toggleContentType(p.key, t)}
                                                        className={`text-[11px] px-2.5 py-1 rounded-full border transition ${
                                                            anchors[p.key].metrics.contentType.includes(t)
                                                                ? "bg-aura-violet/20 border-aura-violet text-aura-text"
                                                                : "border-aura-violet/15 text-aura-muted bg-white"
                                                        }`}
                                                    >
                                                        {t}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-between pt-2">
                            <button onClick={() => setStep(1)} className="px-5 py-2.5 text-sm text-aura-muted hover:text-aura-text transition" data-testid="onboarding-back-2-btn">
                                ← Back
                            </button>
                            <motion.button
                                whileTap={{ scale: 0.97 }}
                                onClick={() => setStep(3)}
                                className="px-6 py-3 rounded-lg bg-gradient-to-r from-aura-violet to-aura-glow text-white font-dm-sans font-medium glow-violet-soft btn-ripple flex items-center gap-2"
                                data-testid="onboarding-next-2-btn"
                            >
                                Review <ArrowRight className="w-4 h-4" />
                            </motion.button>
                        </div>
                    </>
                )}

                {step === 3 && (
                    <>
                        <h2 className="font-syne text-2xl">Ready to compute your AURA</h2>
                        <p className="text-sm text-aura-body">
                            Our GPT-5.2 engine will analyze your profile across 6 dimensions, generate optimized bios,
                            and produce a 7-day action plan.
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {[
                                { l: "Goal", v: goal },
                                { l: "Niche", v: primaryContentNiche || "—" },
                                { l: "Years", v: yearsOfExperience },
                                { l: "Anchors", v: Object.values(anchors).filter((a) => a.url).length },
                                { l: "Brand feel", v: currentBrandFeel || "—" },
                                { l: "Description", v: `${description.length} chars` },
                            ].map((s) => (
                                <div key={s.l} className="bg-aura-elevate/60 border border-aura-violet/10 rounded-lg p-3">
                                    <div className="font-jetbrains-mono text-[10px] uppercase tracking-wider text-aura-muted">{s.l}</div>
                                    <div className="font-syne text-base mt-1 truncate">{s.v}</div>
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-between pt-2">
                            <button onClick={() => setStep(2)} className="px-5 py-2.5 text-sm text-aura-muted hover:text-aura-text transition" data-testid="onboarding-back-3-btn">
                                ← Back
                            </button>
                            <motion.button
                                whileTap={{ scale: 0.97 }}
                                onClick={submit}
                                disabled={submitting}
                                className="px-6 py-3 rounded-lg bg-gradient-to-r from-aura-violet via-aura-glow to-aura-cyan text-white font-dm-sans font-medium glow-violet btn-ripple flex items-center gap-2 disabled:opacity-60"
                                data-testid="onboarding-submit-btn"
                            >
                                {submitting ? (
                                    <>
                                        <div className="cyber-spinner !w-4 !h-4" /> Computing AURA…
                                    </>
                                ) : (
                                    <>
                                        <Sparkles className="w-4 h-4" /> Compute my AURA
                                    </>
                                )}
                            </motion.button>
                        </div>
                    </>
                )}
            </motion.div>
        </Layout>
    );
}

